import type { ApolloClient, MutationUpdaterFn } from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import type { UpdateCurrentLocation2Success } from '@seek/ca-graphql-schema/types';
import type { Language, Locale } from '@seek/ca-myprofile-utils';

import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import type { Page } from 'src/types';
import * as Schema from 'src/types/graphql';

export const updateCacheUpdatePersonalDetails =
  (
    languageCode: Language,
    countryCode: Locale,
    zone: Zone,
  ): MutationUpdaterFn<Schema.UpdatePersonalDetailsMutation> =>
  (cache, { data }) => {
    const _id = data?.updatePersonalDetails?.viewer?._id;
    const personalDetails =
      data?.updatePersonalDetails?.viewer?.personalDetails;

    const getData = () => {
      if (
        data?.updateCurrentLocation2 &&
        'viewer' in data?.updateCurrentLocation2
      ) {
        return data?.updateCurrentLocation2 as UpdateCurrentLocation2Success;
      }
    };

    const viewerData = getData();

    const personalDetailsCachedData = getDataFromCache({
      cache,
      query: Schema.GetPersonalDetailsDocument,
      variables: {
        languageCode,
        countryCode,
        zone,
      },
    });

    cache.modify({
      id: cache.identify({
        __typename: 'Candidate',
        _id,
      }),
      fields: {
        personalDetails: () => ({
          nationality: personalDetails?.nationality || null,
          defaultCountryCallingCode: personalDetailsCachedData?.viewer
            ?.personalDetails.defaultCountryCallingCode || { id: '1' },
          ...personalDetails,
        }),
        salaryPreferences2: () => viewerData?.viewer?.salaryPreferences2 || [],
        rightToWork: () => viewerData?.viewer?.rightsToWork || [],
        currentLocation2: () => viewerData?.viewer?.currentLocation2 || null,
        preferredLocations2: () =>
          viewerData?.viewer?.preferredLocations2 || [],
      },
    });
  };

export const generateUpdatePersonalDetailsOptimisticResponse =
  ({
    _id,
    page,
    apolloClient,
    zone,
    isUnifiedSite,
  }: {
    _id: string;
    page: Page;
    apolloClient: ApolloClient<any>;
    zone: Zone;
    isUnifiedSite: boolean;
  }) =>
  ({
    personalDetailsData,
    currentLocation2Data,
    countryCode,
    languageCode: inputLanguageCode,
    mutationMetadata: { updatedCurrentLocation2 },
  }: Schema.UpdatePersonalDetailsMutationVariables & Record<string, any>) => {
    const {
      firstName,
      lastName,
      phoneNumber,
      nationality,
      countryCallingCode,
    } = personalDetailsData;
    const languageCode = inputLanguageCode || 'en';
    const personalDetailsCachedData = getDataFromCache({
      cache: apolloClient.cache,
      query: Schema.GetPersonalDetailsDocument,
      variables: {
        countryCode,
        languageCode,
        zone,
      },
    });

    const getViewerSalaryPreferenceRightToWorkPrefLocs = () => {
      switch (page) {
        case 'create-resume-page': {
          return {
            salaryPreferences2: [],
            rightsToWork: [],
            preferredLocations2: [],
          };
        }
        default: {
          const salaryPreferenceCachedData = getDataFromCache({
            cache: apolloClient.cache,
            query: Schema.GetSalaryPreferencesDocument,
            variables: {
              countryCode,
              languageCode,
            },
          });

          const rightToWorkCachedData = getDataFromCache({
            cache: apolloClient.cache,
            query: Schema.GetRightsToWorkDocument,
            variables: {
              zone,
              languageCode,
              isUnifiedSite,
            },
          });

          const preferredLocations2CachedData = getDataFromCache({
            cache: apolloClient.cache,
            query: Schema.GetPreferredLocations2Document,
            variables: {
              zone,
              languageCode,
            },
          });

          return {
            salaryPreferences2:
              salaryPreferenceCachedData?.viewer?.salaryPreferences2 || [],
            rightsToWork: rightToWorkCachedData?.viewer?.rightsToWork || [],
            preferredLocations2:
              preferredLocations2CachedData?.viewer?.preferredLocations2 || [],
          };
        }
      }
    };

    const { personalDetails, currentLocation2: currentLocation2CachedData } =
      personalDetailsCachedData?.viewer || {};
    const { currentLocation: currentLocation2Input } = currentLocation2Data;

    return {
      __typename: 'Mutation',
      updatePersonalDetails: {
        __typename: 'UpdatePersonalDetailsPayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          personalDetails: {
            __typename: 'PersonalDetails',
            defaultCountryCallingCode:
              personalDetails?.defaultCountryCallingCode || { id: '1' },
            firstName,
            lastName,
            phoneNumber,
            nationality: nationality || null,
            countryCallingCode,
          },
        },
      },
      updateCurrentLocation2: {
        __typename: 'UpdateCurrentLocation2Success',
        viewer: {
          __typename: 'Candidate',
          _id,
          currentLocation2: {
            __typename: 'Location2',
            id: currentLocation2Input.id,
            shortDescription:
              updatedCurrentLocation2?.shortName ||
              currentLocation2CachedData?.shortDescription ||
              '',
            description:
              updatedCurrentLocation2?.text ||
              currentLocation2CachedData?.description ||
              '',
            kind:
              updatedCurrentLocation2?.kind ||
              currentLocation2CachedData?.kind ||
              '',
            countryCode:
              updatedCurrentLocation2?.countryCode ||
              currentLocation2CachedData?.countryCode ||
              '',
          },
          ...getViewerSalaryPreferenceRightToWorkPrefLocs(),
        },
      },
    };
  };
